/**
 * Cliente HTTP centralizado para a morpherd-api.
 *
 * - Envia cookies httpOnly (`credentials: 'include'`).
 * - Em 401, dispara um unico refresh single-flight e enfileira as demais requests.
 * - Em falha de refresh, emite o evento global `auth:expired` e propaga erro.
 * - Normaliza erros em `ApiError`.
 */

import type { ApiErrorBody, ApiErrorCode } from './types'
import i18n from '@/i18n'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

if (!BASE_URL && import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn('[httpClient] VITE_API_BASE_URL nao definido. Copie .env.example -> .env')
}

const REFRESH_PATH = '/api/auth/refresh/'
const AUTH_EXPIRED_EVENT = 'auth:expired'
const API_ERROR_EVENT = 'api:error'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface HttpOptions {
  query?: Record<string, string | number | boolean | undefined | null>
  headers?: Record<string, string>
  signal?: AbortSignal
  /** Pula o interceptor de refresh — usado internamente pelo proprio refresh. */
  skipAuthRefresh?: boolean
  /** Pula parse de JSON e devolve `Response` cru. */
  raw?: boolean
  /** Suprime a emissao do evento global `api:error`. */
  silent?: boolean
}

export interface HttpRequest<TBody = unknown> extends HttpOptions {
  body?: TBody
}

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly raw: unknown

  constructor(opts: { status: number; code: ApiErrorCode; message: string; raw: unknown }) {
    super(opts.message)
    this.name = 'ApiError'
    this.status = opts.status
    this.code = opts.code
    this.raw = opts.raw
  }
}

function buildUrl(path: string, query?: HttpOptions['query']): string {
  const base = BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')
  const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue
      url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

async function parseError(response: Response): Promise<ApiError> {
  let raw: unknown = null
  try {
    raw = await response.json()
  } catch {
    /* sem corpo JSON */
  }
  const body = (raw ?? {}) as Partial<ApiErrorBody> & Record<string, unknown>
  const code: ApiErrorCode =
    (typeof body.code === 'string' ? body.code : undefined) ?? `HTTP_${response.status}`
  // `description` is the canonical field. `message` and `detail` are legacy fallbacks.
  const firstOf = (v: unknown): string | false =>
    Array.isArray(v) && typeof v[0] === 'string' ? v[0] : false

  const message =
    (typeof body.description === 'string' && body.description) ||
    (typeof body.message === 'string' && body.message) ||
    (typeof body.detail === 'string' && body.detail) ||
    firstOf(body.detail) ||
    firstOf(body.non_field_errors) ||
    response.statusText ||
    `HTTP ${response.status}`
  return new ApiError({ status: response.status, code, message, raw })
}

// ── Single-flight refresh ─────────────────────────────────────────────────────

let refreshInFlight: Promise<void> | null = null

function emitAuthExpired() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
}

export function onAuthExpired(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = () => listener()
  window.addEventListener(AUTH_EXPIRED_EVENT, handler)
  return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler)
}

export function onApiError(listener: (error: ApiError) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<ApiError>
    if (customEvent.detail) listener(customEvent.detail)
  }
  window.addEventListener(API_ERROR_EVENT, handler)
  return () => window.removeEventListener(API_ERROR_EVENT, handler)
}

function emitApiError(error: ApiError) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<ApiError>(API_ERROR_EVENT, { detail: error }))
}

async function performRefresh(): Promise<void> {
  if (refreshInFlight) {
    return refreshInFlight
  }
  refreshInFlight = (async () => {
    const response = await fetch(buildUrl(REFRESH_PATH), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      emitAuthExpired()
      throw await parseError(response)
    }
  })().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// ── Core ──────────────────────────────────────────────────────────────────────

async function execute<TResp>(
  method: HttpMethod,
  path: string,
  req: HttpRequest = {},
): Promise<TResp> {
  const { body, query, headers, signal, skipAuthRefresh, raw } = req
  const { silent = false } = req

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.language || 'pt-BR',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    signal,
  }
  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  const url = buildUrl(path, query)
  let response = await fetch(url, init)

  if (response.status === 401 && !skipAuthRefresh && !path.startsWith('/api/auth/')) {
    try {
      await performRefresh()
    } catch (err) {
      throw err instanceof ApiError ? err : await parseError(response)
    }
    response = await fetch(url, init)
  }

  if (!response.ok) {
    if (response.status === 401) emitAuthExpired()
    const error = await parseError(response)
    if (!silent) emitApiError(error)
    throw error
  }

  if (raw) return response as unknown as TResp
  if (response.status === 204) return undefined as TResp
  const text = await response.text()
  if (!text) return undefined as TResp
  return JSON.parse(text) as TResp
}

export const http = {
  get: <TResp>(path: string, opts?: HttpOptions) => execute<TResp>('GET', path, opts),
  post: <TResp, TBody = unknown>(path: string, body?: TBody, opts?: HttpOptions) =>
    execute<TResp>('POST', path, { ...opts, body }),
  put: <TResp, TBody = unknown>(path: string, body?: TBody, opts?: HttpOptions) =>
    execute<TResp>('PUT', path, { ...opts, body }),
  patch: <TResp, TBody = unknown>(path: string, body?: TBody, opts?: HttpOptions) =>
    execute<TResp>('PATCH', path, { ...opts, body }),
  delete: <TResp>(path: string, opts?: HttpOptions) => execute<TResp>('DELETE', path, opts),
}

export const __internal = { performRefresh, AUTH_EXPIRED_EVENT, API_ERROR_EVENT }
